import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { formatApiError } from '../utils/errorMessage';

// Public page behind an emailed invoice's Pay now link. No sign-in: the link
// itself is the key. Shows the invoice number and amount due, and sends the
// payer to Stripe Checkout on the business's own Stripe account.
function apiBase() {
  const { hostname, origin, protocol } = window.location;
  const local = hostname === 'localhost' || hostname === '127.0.0.1';
  if (!local) return `${origin}/api/pay`;
  if (process.env.REACT_APP_API_URL) return `${process.env.REACT_APP_API_URL}/pay`;
  return `${protocol}//${hostname}:5000/api/pay`;
}

const money = (n, c) => {
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: (c || 'usd').toUpperCase() }).format(Number(n) || 0); } catch (e) { return `$${Number(n || 0).toFixed(2)}`; }
};

export default function PayInvoice() {
  const { token } = useParams();
  const location = useLocation();
  const justPaid = new URLSearchParams(location.search).get('paid') === '1';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${apiBase()}/${encodeURIComponent(token)}`)
      .then((res) => setData(res.data))
      .catch((err) => setError(formatApiError(err, 'This payment link is not valid.')));
  }, [token]);

  const pay = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${apiBase()}/${encodeURIComponent(token)}/checkout`);
      window.location.assign(res.data.url);
    } catch (err) {
      setError(formatApiError(err, 'Payment could not start. Please try again.'));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow p-6 w-full max-w-md">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4" role="alert">{error}</div>}
        {!data && !error && <p className="text-gray-600">Loading...</p>}
        {data && (
          <>
            <h1 className="text-xl font-semibold">{data.business.name}</h1>
            {data.business.sub_line && <p className="text-sm text-gray-500">{data.business.sub_line}</p>}
            <div className="mt-4 border-t pt-4 space-y-1 text-sm">
              <p>Invoice <strong>{data.invoice.number}</strong></p>
              {data.invoice.due_date && <p>Due {data.invoice.due_date}</p>}
              <p className="text-2xl font-semibold mt-2">{money(data.invoice.amount_due, data.invoice.currency)}</p>
            </div>
            {data.paid || justPaid ? (
              <p className="mt-4 text-green-700 font-medium">{data.paid ? 'This invoice is paid. Thank you!' : 'Thank you! Your payment is being processed.'}</p>
            ) : data.can_pay ? (
              <button type="button" onClick={pay} disabled={busy} className="mt-6 w-full bg-blue-600 text-white rounded py-3 font-semibold disabled:opacity-50">
                {busy ? 'Opening secure payment...' : 'Pay now'}
              </button>
            ) : (
              <p className="mt-4 text-gray-700">Online payment is not available for this invoice. Please contact the business{data.business.email ? ` at ${data.business.email}` : ''}.</p>
            )}
            <p className="mt-6 text-xs text-gray-400">Payments are processed securely by Stripe.</p>
          </>
        )}
      </div>
    </div>
  );
}